import { Location } from '@angular/common';
import {
  DestroyRef,
  EnvironmentProviders,
  Injectable,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';

/**
 * The history-state key holding how many in-app pages sit behind an entry. It lives on the entry
 * itself, so a reload or a browser Back/Forward restores the true value: the router copies an
 * entry's own state keys back onto it, while every imperative navigation writes fresh state.
 */
export const IN_APP_DEPTH_STATE_KEY = 'cmiInAppDepth';

/** Pages outside the signed-in app; an entry left from one of these is never a Back target. */
const OUTSIDE_APP = /^\/login(?:[/?#]|$)/;

interface PendingNavigation {
  readonly id: number;
  /** A browser Back/Forward or a reload: the entry already carries its own depth. */
  readonly restored: boolean;
  readonly replaceUrl: boolean;
  readonly skipLocationChange: boolean;
  /** The state of the entry this navigation leaves, to put back if the navigation is refused. */
  readonly leftState: Readonly<Record<string, unknown>>;
}

/**
 * The one answer to "go back" for drill-in pages (GF-6405). Back returns to the page the user was
 * actually on, the way the browser's Back does, not to a route each page guesses: Home → insight
 * → Back lands on Home, and a chain of drills unwinds one step at a time with each page's URL,
 * query and scroll intact. Only an entry with no in-app page behind it (a new tab, a bookmark, an
 * email link) uses the page's fallback route.
 */
@Injectable({ providedIn: 'root' })
export class InAppHistory {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  private depth = 0;
  private currentUrl: string | null = null;
  private pending: PendingNavigation | null = null;

  /** Starts recording; {@link provideInAppHistory} calls it before the first navigation. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.onRouterEvent(event));
  }

  /** Whether an in-app page sits behind the current one in this tab's history. */
  canGoBack(): boolean {
    return this.depth > 0;
  }

  /**
   * Returns to the previous in-app page, or opens `fallbackUrl` when there is none. The fallback
   * is a full URL (path, query and encoded segments), so it goes through `navigateByUrl`.
   */
  back(fallbackUrl: string): void {
    if (this.canGoBack()) {
      this.location.back();
      return;
    }
    void this.router.navigateByUrl(fallbackUrl);
  }

  private onRouterEvent(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.pending = this.describe(event);
      return;
    }
    if (event instanceof NavigationEnd) {
      this.settle(event);
      return;
    }
    if (event instanceof NavigationCancel || event instanceof NavigationError) {
      this.restoreAfterRefusal(event.id);
      return;
    }
    if (event instanceof NavigationSkipped && this.pending?.id === event.id) {
      this.pending = null;
    }
  }

  private describe(start: NavigationStart): PendingNavigation {
    const navigation = this.router.currentNavigation();
    const extras = navigation?.id === start.id ? navigation.extras : {};
    return {
      id: start.id,
      restored: start.navigationTrigger !== 'imperative' || start.restoredState != null,
      replaceUrl: !!extras.replaceUrl,
      skipLocationChange: !!extras.skipLocationChange,
      leftState: this.currentState(),
    };
  }

  /**
   * A refused navigation (a guard, an error) leaves the user on the page they were on, but the
   * router rewrites the active entry's state with only its navigationId — also for a
   * skipLocationChange navigation. Put back the keys that entry held (its depth, an opener's
   * backRoute), so a later reload or browser Back/Forward onto it still knows what lies behind it.
   * A refused browser Back/Forward leaves the browser on the entry it moved to, so that entry keeps
   * its own depth: it is that slot's place in the tab's history that decides what Back may reach.
   */
  private restoreAfterRefusal(id: number): void {
    const pending = this.pending?.id === id ? this.pending : null;
    this.pending = null;
    if (!pending) return;
    if (pending.restored) this.depth = depthIn(pending.leftState);
    const state = this.currentState();
    const intact =
      state[IN_APP_DEPTH_STATE_KEY] === this.depth &&
      Object.keys(pending.leftState).every((key) => key in state);
    if (intact) return;
    this.location.replaceState(this.location.path(true), '', {
      ...pending.leftState,
      ...state,
      [IN_APP_DEPTH_STATE_KEY]: this.depth,
    });
  }

  private settle(end: NavigationEnd): void {
    const pending = this.pending?.id === end.id ? this.pending : null;
    this.pending = null;
    if (pending?.skipLocationChange) return;

    const leftUrl = this.currentUrl;
    this.currentUrl = end.urlAfterRedirects;

    if (!pending || pending.restored) {
      this.depth = depthIn(this.currentState());
      return;
    }
    // The router replaces rather than pushes when asked to, or when the URL did not change.
    const replaced = pending.replaceUrl || leftUrl === end.urlAfterRedirects;
    if (!replaced) {
      this.depth = leftUrl !== null && !OUTSIDE_APP.test(leftUrl) ? this.depth + 1 : 0;
    }
    this.stampDepth();
  }

  /** Writes the depth onto the entry the router just wrote, keeping every key it carries. */
  private stampDepth(): void {
    const state = this.currentState();
    if (state[IN_APP_DEPTH_STATE_KEY] === this.depth) return;
    this.location.replaceState(this.location.path(true), '', {
      ...state,
      [IN_APP_DEPTH_STATE_KEY]: this.depth,
    });
  }

  private currentState(): Readonly<Record<string, unknown>> {
    const state = this.location.getState();
    return state !== null && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  }
}

/** Records in-app history from the app's first navigation, so Back knows what lies behind it. */
export function provideInAppHistory(): EnvironmentProviders {
  return provideAppInitializer(() => inject(InAppHistory).start());
}

/** The in-app depth an entry's state records; 0 when it records none. */
function depthIn(state: Readonly<Record<string, unknown>>): number {
  const value = state[IN_APP_DEPTH_STATE_KEY];
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;
}
